export default function EdinaTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Edina Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Edina travelers actually move helps us anticipate the passport urgencies that arise here. From Galleria fashion events to corporate corridor sprints and school trip coordination, southwest metro travel operates on a unique rhythm.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          {/* MSP Airport */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">MSP — Minneapolis-Saint Paul International Airport</h3>
                <p className="text-gray-400 text-xs">About 8 miles from Edina — 12 minutes by car</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Edina is exceptionally close to MSP — just 8 miles via Highway 62 or Highway 100. This proximity is a major advantage for Edina's frequent international travelers. With nonstop service to Amsterdam, London, Paris, Tokyo, and Reykjavik, many residents connect directly to Europe and Asia with minimal ground transportation. The airport's location in the heart of the metro means an Edina resident can check their passport status on Monday morning and be on a transatlantic flight by Thursday evening.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Because MSP is so close, many Edina travelers book international flights with minimal lead time. Check your passport expiration before every travel planning conversation, not after tickets are purchased.
              </p>
            </div>
          </div>

          {/* Fashion & Retail Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-shopping-bag-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Galleria &amp; 50th and France Shopping</h3>
                <p className="text-gray-400 text-xs">International retail travel for fashion and design professionals</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Edina's retail districts attract fashion-conscious travelers who extend their shopping internationally. Paris fashion weeks, London design shows, Milan shopping trips, and Tokyo boutique visits are common. These trips are often spontaneous with 2–3 weeks notice — a boutique owner announces a private sale, a designer invites you to an exclusive preview, a friend plans a wedding in Tuscany.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Fashion and retail travelers should keep passports current at all times. Spontaneous invitations and last-minute sales events require immediate travel readiness.
              </p>
            </div>
          </div>

          {/* Edina Schools & Student Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Edina Schools &amp; Student Travel</h3>
                <p className="text-gray-400 text-xs">Language immersion, study abroad, and international programs</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Edina's excellent public and private schools offer extensive international programs. Students travel for language immersion, cultural exchanges, and academic competitions. Parents often discover passport needs with only weeks before departure: a school trip to France, a language immersion program in Spain, a sports tournament in Canada. Child passports expire in 5 years, not 10, which catches many Edina parents off guard.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check every family member's passport at the start of the school year. Children's passports expire in 5 years, not 10. Plan renewals 6 months before school trips.
              </p>
            </div>
          </div>

          {/* Corporate & Professional Travel */}
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-briefcase-4-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Corporate &amp; Professional Travel</h3>
                <p className="text-gray-400 text-xs">Executives, physicians, and attorneys with international clients</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Edina's professional community includes executives, physicians, and attorneys with international clients and conferences. These professionals often receive travel assignments with 2–3 weeks notice: a client meeting in London, a medical conference in Vienna, a legal arbitration in Singapore. The concentration of corporate offices near Southdale and the Edina business corridor means international business travel is a constant fixture of the local economy.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Professional travelers should check passport expiration quarterly. Set calendar reminders for the entire team. Business travel is rarely planned months in advance.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-building-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Corporate Event &amp; Conference Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Edina's professional community hosts and attends major industry conferences and events that draw international attendees. Event organizers, keynote speakers, and corporate attendees flying in from abroad — or Edina professionals traveling to partner events overseas — sometimes discover passport issues during event planning. We accommodate urgent corporate timelines with same-day document review and prioritized submission.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}